// Dependencies
import { FC, useContext, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import {
  Button,
  Avatar,
  Pagination,
  Stack,
  CircularProgress,
  Box,
  Chip,
  Menu,
  MenuItem,
  Checkbox,
  IconButton,
  FormControlLabel,
  Switch
} from '@mui/material';
import SettingIcon from '@mui/icons-material/Settings';
import RecoverIcon from '@mui/icons-material/Refresh';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

// Context
import { AppContext } from '../../../context';

// Components
import { IColumn, Table, UserDialog } from '../../../components';

// Resources
import { ISortBy, ORDER, ROLE } from '../../../resource';
import apis from '../../../apis';
import {ACCESS_ADMIN_TOKEN_KEY, ACCESS_TOKEN_KEY, Pages, ROUTES} from '../../../contants';
import { useToastContext } from '../../../components/Toast/toastContext';
import { ContentCopy, Check } from '@mui/icons-material';
import { useDebounce } from '../../../hooks/useDebounce';
import { useNavigate } from 'react-router-dom';
import {useAlertContext} from '../../../components/Alert/alertContext';
import {recoverUser} from '../../../apis/user.apis';
import moment from 'moment/moment';

// Utils
import { useUrlParams } from '../../../utils/urlParams';

// Styles
// import styles from './styles';

// Constants
const pageLimit = 15;

let timeoutId;

// Export user list page
export const UserListPage: FC = () => {
  const navigate = useNavigate();

  // URL params hook
  const { updateUrlParams, updateUrlParamsAfterApiResponse, initializeFilters, getUrlParams, restoreFromStorage } = useUrlParams('users');

  // States
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [sort, setSort] = useState<ISortBy>({
    field: 'firstName',
    order: ORDER.ASC,
  });
  const [loginAsLoading, setLoginAsLoading] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const latestRequestId = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Context
  const {
    account,
    setAccount,
    dialogs,
    user,
    users,
    selectedUsers,
    search,
    setSearch,
    showDeleted,
    setShowDeleted,
    retentionConfig
  } = useContext(AppContext);
  const debouncedSearch = useDebounce(search);

  const { activeToast } = useToastContext();
  const { activeAlert } = useAlertContext();
  const activeToastRef = useRef(activeToast);
  const usersRef = useRef(users);

  // Update the refs when they change
  useEffect(() => {
    activeToastRef.current = activeToast;
  }, [activeToast]);

  useEffect(() => {
    usersRef.current = users;
  }, [users]);

  // Initialize filters from URL or localStorage
  useEffect(() => {
    console.log('Initializing Users filters...');
    
    // Get current URL parameters
    const urlParams = getUrlParams();
    console.log('Current URL params:', urlParams);
    
    // Get stored filters
    const storedParams = restoreFromStorage();
    console.log('Stored params:', storedParams);
    
    // Determine which parameters to use
    let finalParams;
    
    if (Object.keys(urlParams).length > 0) {
      // URL has parameters - use them
      console.log('Using URL parameters');
      finalParams = urlParams;
    } else if (Object.keys(storedParams).length > 0) {
      // No URL params but stored params exist - use stored params
      console.log('Using stored parameters');
      finalParams = storedParams;
    } else {
      // No stored params - use defaults
      console.log('Using default parameters');
      finalParams = {
        page: 1,
        sort: 'firstName_asc',
        archived: false,
      };
    }
    
    console.log('Final params to apply:', finalParams);
    
    // Apply the final parameters
    setPageNumber(finalParams.page || 1);
    
    if (finalParams.sort) {
      const [field, order] = finalParams.sort.split('_');
      setSort({ field, order: order === 'asc' ? ORDER.ASC : ORDER.DESC });
    }
    
    if (finalParams.archived !== undefined) {
      setShowDeleted(finalParams.archived);
    }
    
    // Handle search parameter - only restore from URL params, not localStorage
    if (finalParams.search !== undefined && finalParams.search !== '') {
      setSearch(finalParams.search);
    }
    
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (copiedId) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setCopiedId(null);
      }, 5000);
    }
  }, [copiedId]);

  const handleDeleteUserForever = async (userId: string) => {
    activeAlert({
      type: 'info',
      text: "Are you sure to remove this user forever?",
    }, async () => {
      try {
        const res = await apis.user.removeUserForever(userId);
        activeToastRef.current({
          type: 'success',
          text: res.msg,
        });
      } catch (e) {
        activeToastRef.current({
          type: 'error',
          text: 'Failed to delete user. Please try again.',
        });
      }
    });
  };

  const handleRecoverUser = async (userId: string) => {
    activeAlert({
      type: 'info',
      text: "Are you sure to recover this user?",
    }, async () => {
      try {
        const res = await apis.user.recoverUser(userId);
        activeToastRef.current({
          type: 'success',
          text: res.msg,
        });
      } catch (e) {
        activeToastRef.current({
          type: 'error',
          text: 'Failed to recover user. Please try again.',
        });
      }
    });
  };

  // Define columns
  const columns: IColumn[] = [
    {
      field: 'profile',
      label: 'Profile',
      sortable: false,
      render: ({ avatar }) => <Avatar src={avatar} />,
    },
    {
      field: 'firstName',
      label: 'Name',
      render: ({ firstName, lastName }) => `${firstName || ''} ${lastName || ''}`,
    },
    {
      field: 'email',
      label: 'Email',
    },
    {
      field: 'id',
      label: 'Customer ID',
      render: ({ id }) => {
        return (
          <Box display="flex" alignItems="center">
            <Chip label={id} />
            <Box ml={5} sx={{ cursor: 'pointer' }} onClick={() => handleCopyCustomerId(id)}>
              {copiedId === id ? <Check color="success" /> : <ContentCopy width={6} height={6} />}
            </Box>
          </Box>
        )
      }
    },
    {
      field: 'role',
      label: 'Access Level',
      render: (row) => (row.role === ROLE.ADMIN ? 'Administrator' : 'Customer'),
    },
    ...(showDeleted ? [
      {
        field: 'delete_after',
        label: 'Will be deleted after',
        render: (row) => {
          const deletedAfter = retentionConfig.user - moment().diff(row.deletedAt, 'day');
          return `${deletedAfter > 1 ? `after ${deletedAfter} days` : deletedAfter === 1 ? 'tomorrow' : '' }`;
        }
      },
      {
        field: 'deletedActions',
        label: 'Actions',
        render: (row) => (
          <Box display="flex">
            {/*@ts-ignore*/}
            <IconButton variant="text" size="medium" onClick={() => handleRecoverUser(row.id)}>
              <RecoverIcon />
            </IconButton>
            {/*@ts-ignore*/}
            <IconButton variant="text" size="medium" onClick={() => handleDeleteUserForever(row.id)}>
              <DeleteForeverIcon />
            </IconButton>
          </Box>
        )
      }
    ] : [
      {
        field: 'pages',
        label: 'Pages',
        render: (row) => (
          <>
            {/*@ts-ignore*/}
            <IconButton variant="text" size="medium" onClick={() => navigate(ROUTES.USER.CONFIG.replace(':id', row.id))}>
              <SettingIcon />
            </IconButton>
          </>
        ),
      },
      {
        field: '',
        label: 'Login As',
        sortable: false,
        render: (row) => (
          <Button size="small" sx={{ minWidth: 110 }} disabled={account?.id === row.id} onClick={() => handleLoginAs(row.id)}>
            {loginAsLoading && loginAsLoading === row.id && <CircularProgress size={16} sx={{ mr: 2, color: 'white' }} />}
            Login As
          </Button>
        )
      },
    ])
  ];

  // Calc page count
  const pageCnt = useMemo(() => {
    return Math.ceil((users.pagination?.total || 0) / pageLimit);
  }, [users.pagination]);

  const handleCopyCustomerId = (id: string) => {
    setCopiedId(id);
    navigator.clipboard.writeText(id);
  };

  // Login as
  const handleLoginAs = (id: string) => {
    setLoginAsLoading(id);
    apis.auth
      .adminLoginAsUser(id)
      .then((res) => {
        setLoginAsLoading(null);
        const { user, token } = res;
        setAccount(user);
        const adminToken = localStorage.getItem(ACCESS_TOKEN_KEY) || "";
        localStorage.setItem(ACCESS_TOKEN_KEY, token);
        localStorage.setItem(ACCESS_ADMIN_TOKEN_KEY, adminToken);
        activeToastRef.current({
          type: 'success',
          text: `Logged in as ${user.email}`,
        });
      })
      .catch((err) => {
        activeToastRef.current({
          type: 'error',
          text: err.msg || 'Failed to login',
        });
      })
      .finally(() => {
        setLoginAsLoading(null);
      })
  };

  // Sort handler
  const handleSort = (value: ISortBy) => {
    setSort(value);
    updateUrlParams({ sort: `${value.field}_${value.order}` });
  };

  // Close user dialog handler
  const handleCloseUserDialog = () => {
    dialogs.user.setVisible(false);
  };

  // Selected change handler
  const handleChangeSelected = (selected: (string | number)[]) => {
    selectedUsers.setValue(selected as string[]);
  };

  // Page change handler
  const handlePageChange = (_, page: number) => {
    setPageNumber(page);
    selectedUsers.setValue([]);
    updateUrlParams({ page });
  };

  // Update URL params when showDeleted changes
  useEffect(() => {
    if (isInitialized) {
      setPageNumber(1);
      updateUrlParams({ page: 1, archived: showDeleted });
    }
  }, [showDeleted, isInitialized]);

  // Fetch data when dependencies change
  useEffect(() => {
    if (!isInitialized) return;

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    latestRequestId.current += 1;
    const requestId = latestRequestId.current;

    const fetchData = async () => {
      try {
        await usersRef.current.fetchData({
          options: {
            limit: pageLimit,
            skip: (pageNumber - 1) * pageLimit,
            sort: {
              [sort.field]: sort.order,
            },
            search: debouncedSearch,
            archived: showDeleted ? 1 : 0,
          },
          signal: controller.signal,
          requestId,
        });
      } catch (err: any) {
        if (err.name === 'CanceledError' || err.message?.includes('canceled') || err?.canceled) {
          // Request was cancelled, do nothing
          return;
        }
        // Show real errors only
        activeToastRef.current({
          type: 'error',
          text: err.message || 'Failed to fetch users',
        });
      }
    };

    fetchData();

    // On unmounted
    return () => {
      selectedUsers.setValue([]);
      dialogs.user.setVisible(false);
    };
  }, [isInitialized, pageNumber, sort, account?.id, showDeleted, debouncedSearch]);

  // On search changed - update URL after successful API response
  useEffect(() => {
    if (isInitialized && debouncedSearch !== undefined) {
      // Update URL params after a short delay to allow API response
      setTimeout(() => {
        updateUrlParamsAfterApiResponse({ search: debouncedSearch });
      }, 100);
    }
  }, [debouncedSearch, isInitialized]);

  // Return user list page
  return (
    <>
      <Stack alignItems="center" spacing={{ xs: 36, md: 66 }}>
        <Table
          stickyHeader
          rowSelectable
          columns={columns}
          data={users.value}
          order={sort.order}
          orderBy={sort.field}
          isLoading={users.isLoading}
          selected={selectedUsers.value}
          contentSize={{
            width: {
              xs: 'calc(100vw - 40px)',
              sm: 'calc(100vw - 80px)',
              md: 'calc(100vw - 256px)',
              lg: 'calc(100vw - 376px)',
            },
          }}
          onSort={handleSort}
          onChangeSelected={handleChangeSelected}
        />
        {users.pagination && pageCnt > 1 && (
          <Pagination page={pageNumber} count={pageCnt} onChange={handlePageChange} />
        )}
      </Stack>
      {dialogs.user.visible && (
        <UserDialog open={dialogs.user.visible} user={user.value} onClose={handleCloseUserDialog} />
      )}
    </>
  );
};
